"use client";

import React from "react";
import Link from "next/link";
import { Search } from "lucide-react";
import { Button } from "@/app/components/ui/button";
import { WEBSITE_NAME } from "@/app/lib/types";

export function HomeHero() {
  const openSearch = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("openSearch"));
    }
  };

  return (
    <section className="relative overflow-hidden py-20 px-4 md:py-32">
      {/* Theme-aware gradient background */}
      <div className="absolute inset-0 bg-linear-to-br from-primary via-primary/90 to-primary/80 dark:from-primary/95 dark:via-primary/90 dark:to-primary/85" />
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.07] dark:opacity-[0.05]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="flex flex-col items-center text-center">
          <h1
            className="text-4xl font-extrabold tracking-tight md:text-6xl lg:text-7xl animate-slide-in-left text-primary-foreground [font-family:var(--font-outfit)]"
            style={{ textShadow: "0 2px 20px rgba(0,0,0,0.15)" }}
          >
            <span>{WEBSITE_NAME}</span>{" "}
            <span className="text-primary-foreground/90">Everything You Need</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/90 md:text-xl animate-slide-in-right">
            A collection of free, powerful online tools. Download, convert,
            compress, generate — all in one place.
          </p>

          {/* Search bar + CTA */}
          <div className="mt-8 flex flex-col items-center gap-4 w-full max-w-2xl animate-scale-in">
            <button
              type="button"
              onClick={openSearch}
              className="flex w-full items-center gap-3 rounded-full border-2 border-primary-foreground/30 bg-primary-foreground/10 px-5 py-3.5 text-left text-primary-foreground/90 transition-all duration-200 hover:border-primary-foreground/50 hover:bg-primary-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary-foreground/50"
            >
              <Search className="h-5 w-5 shrink-0 text-primary-foreground/70" />
              <span className="text-sm md:text-base">Search tools... (⌘K)</span>
            </button>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/download">
                <Button
                  size="lg"
                  className="rounded-full bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold"
                >
                  Start Downloading
                </Button>
              </Link>
              <Link href="#features">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-2 border-primary-foreground/50 bg-transparent text-primary-foreground hover:bg-primary-foreground/20 hover:border-primary-foreground"
                >
                  Explore Features
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-10 flex flex-wrap justify-center gap-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <div className="text-center">
              <span className="block text-2xl font-bold text-primary-foreground md:text-3xl">
                50+
              </span>
              <span className="text-sm text-primary-foreground/80">Free Tools</span>
            </div>
            <div className="text-center">
              <span className="block text-2xl font-bold text-primary-foreground md:text-3xl">
                100%
              </span>
              <span className="text-sm text-primary-foreground/80">Free</span>
            </div>
            <div className="text-center">
              <span className="block text-2xl font-bold text-primary-foreground md:text-3xl">
                0
              </span>
              <span className="text-sm text-primary-foreground/80">Sign-up Required</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
